import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { AxiosError } from "axios";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { resendInvoiceAction, updateInvoiceAction, updateInvoiceRecipientsAction } from "../actions/adminInvoice";
import type { IInvoice } from "../../create/interfaces/invoice.interfaces";
import { invalidateAfterInvoiceChange } from "../utils/invoiceQueryInvalidation";

/** Must match RESEND_COOLDOWN_MS on the backend (InvoiceService). */
const RESEND_COOLDOWN_MS = 60 * 1000;

const toMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) return (error.response?.data as any)?.message || error.message || fallback;
  if (error instanceof Error) return error.message;
  return fallback;
};

const useCompanyId = (): number | null => {
  const company = useSelector((state: any) => state.userState?.company);
  return company?.id ?? null;
};

const useInvoiceInvalidator = () => {
  const queryClient = useQueryClient();
  return (invoiceUid: string, jobEventUids: string[] = [], quoteId?: number | null) => {
    invalidateAfterInvoiceChange(queryClient, { invoiceUid, jobEventUids, quoteId });
  };
};

export const useResendInvoice = () => {
  const companyId = useCompanyId();
  const { showSnackbar } = useGlobalSnackbar();
  const invalidate = useInvoiceInvalidator();

  const mutation = useMutation({
    mutationFn: ({ invoiceUid }: { invoiceUid: string; jobEventUids?: string[]; quoteId?: number | null }) => {
      if (!companyId) throw new Error("Company ID is required");
      return resendInvoiceAction(invoiceUid, companyId);
    },
    onSuccess: (_data, variables) => {
      invalidate(variables.invoiceUid, variables.jobEventUids, variables.quoteId);
      showSnackbar("Invoice resent", "success", 4000, "top-center");
    },
    onError: error => {
      showSnackbar(toMessage(error, "Could not resend invoice"), "error", 6000, "top-center");
    },
  });

  return { resendInvoice: mutation.mutate, isPending: mutation.isPending };
};

/**
 * Computes the remaining resend cooldown from the invoice's lastResendAt timestamp.
 * Ticks every second while active so the button label/counter stays live.
 */
export const useResendCooldown = (lastResendAt: string | Date | null | undefined, cooldownMs: number = RESEND_COOLDOWN_MS) => {
  const expiresAt = useMemo(() => {
    if (!lastResendAt) return 0;
    const ts = new Date(lastResendAt).getTime();
    return Number.isNaN(ts) ? 0 : ts + cooldownMs;
  }, [lastResendAt, cooldownMs]);

  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    if (!expiresAt || expiresAt <= Date.now()) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  const remainingMs = Math.max(0, expiresAt - now);
  return {
    remainingMs,
    remainingSec: Math.ceil(remainingMs / 1000),
    isCoolingDown: remainingMs > 0,
  };
};

export const useUpdateInvoice = () => {
  const companyId = useCompanyId();
  const { showSnackbar } = useGlobalSnackbar();
  const invalidate = useInvoiceInvalidator();

  const mutation = useMutation({
    mutationFn: ({ invoiceUid, patch }: { invoiceUid: string; patch: Partial<IInvoice>; jobEventUids?: string[]; quoteId?: number | null }) => {
      if (!companyId) throw new Error("Company ID is required");
      return updateInvoiceAction(invoiceUid, companyId, patch);
    },
    onSuccess: (_data, variables) => {
      invalidate(variables.invoiceUid, variables.jobEventUids, variables.quoteId);
      showSnackbar("Invoice updated", "success", 3000, "top-center");
    },
    onError: error => {
      showSnackbar(toMessage(error, "Could not update invoice"), "error", 6000, "top-center");
    },
  });

  return { updateInvoice: mutation.mutate, isPending: mutation.isPending };
};

export const useUpdateInvoiceRecipients = () => {
  const companyId = useCompanyId();
  const { showSnackbar } = useGlobalSnackbar();
  const invalidate = useInvoiceInvalidator();

  const mutation = useMutation({
    mutationFn: ({ invoiceUid, emailRecipients }: { invoiceUid: string; emailRecipients: IInvoice["emailRecipients"]; jobEventUids?: string[]; quoteId?: number | null }) => {
      if (!companyId) throw new Error("Company ID is required");
      return updateInvoiceRecipientsAction(invoiceUid, companyId, emailRecipients);
    },
    onSuccess: (_data, variables) => {
      invalidate(variables.invoiceUid, variables.jobEventUids, variables.quoteId);
      showSnackbar("Recipients updated", "success", 3000, "top-center");
    },
    onError: error => {
      showSnackbar(toMessage(error, "Could not update recipients"), "error", 6000, "top-center");
    },
  });

  return { updateRecipients: mutation.mutate, isPending: mutation.isPending };
};
